import { useState } from "react";
import { ModalDialog } from "@/components/data-display/modal-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { useAuth } from "@/features/authentication";

interface LongListFeedbackModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  longListId: string | null;
  dealId: string | null;
}

export function LongListFeedbackModal({
  open,
  onClose,
  onConfirm,
  longListId,
  dealId,
}: LongListFeedbackModalProps) {
  const { userInfo } = useAuth();
  const [feedback, setFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFeedbackChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFeedback(e.target.value);
  };

  const handleConfirm = async () => {
    if (feedback.trim().length < 3) {
      return;
    }

    setIsSubmitting(true);

    try {
      await sendTrackDataServer({
        eventName: "VISION_LONGLIST_FEEDBACK_SUBMITTED",
        productType: "PO",
        data: {
          feedback: feedback.trim(),
          longlist_id: longListId ?? "",
          user_id: userInfo?.user?.id ?? null,
          email: userInfo?.profile?.email ?? null,
          deal_id: dealId ?? null,
        },
        url: location.pathname,
      });

      setFeedback("");
      onConfirm();
    } catch (error) {
      console.error("Error submitting feedback:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFeedback("");
    onClose();
  };

  const isValid = feedback.trim().length >= 3;

  return (
    <ModalDialog
      type="button"
      title="Você clicou no botão voltar e está saindo da long list"
      description="Ajude-nos a melhorar! Compartilhe seu feedback sobre por que a sugestão da long list não atendeu suas necessidades."
      open={open}
      handleClose={handleClose}
      handleConfirm={handleConfirm}
      enableButton={isValid && !isSubmitting}
      maxMidth="max-w-2xl p-0"
      showFooter={false}
      titleStyle="text-lg pt-6 px-6 pb-0 zinc-950"
      descriptionStyle="text-sm px-6 text-zinc-500 mt-2"
    >
      <div className="pt-5 pb-6">
        <div className="px-6">
          <div className="mb-4">
            <label
              htmlFor="feedback-input"
              className="block text-sm font-medium leading-6 text-zinc-950 mb-2"
            >
              Feedback
            </label>
            <Textarea
              id="feedback-input"
              value={feedback}
              onChange={handleFeedbackChange}
              placeholder="Digite seu feedback aqui (mínimo 3 caracteres)..."
              className="min-h-[120px] resize-none"
              disabled={isSubmitting}
            />
            <p className="mt-2 text-xs text-zinc-500">
              {feedback.trim().length}/3 caracteres mínimos
            </p>
          </div>
        </div>
        <div className="border-t border-zinc-200 px-6 pt-6">
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              className="min-h-[40px]"
            >
              Permanecer na long list
            </Button>
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={!isValid || isSubmitting}
              className="min-h-[40px]"
            >
              {isSubmitting ? "Enviando..." : "Continuar"}
            </Button>
          </div>
        </div>
      </div>
    </ModalDialog>
  );
}
